
import React from "react";
import { Message } from "@/types/message";
import CollapsedThreadGroup from "./thread/CollapsedThreadGroup";
import LatestThreadGroup from "./thread/LatestThreadGroup";
import RegeneratedLatestThreadGroup from "./thread/RegeneratedLatestThreadGroup";
import { useThreadHelpers } from "./thread/useThreadHelpers";

interface ThreadGroupProps {
  thread: {
    threadId: string;
    messages: Message[];
    latestMessage: Message;
    timestamp: Date;
    hasMultipleVersions: boolean;
  };
  isLatestThread: boolean;
  companyName: string;
  onRegenerateAIResponse?: (messageId: string, tone: string, customInstructions?: string) => Promise<Message | null>;
  isRegenerating?: boolean;
  regeneratingMessageId?: string;
  orderNumber?: string;
  userEmail?: string;
  sellerEmail?: string;
  latestAIMessage?: Message;
  expandedThreads: Record<string, boolean>;
  toggleThread: (threadId: string) => void;
}

const ThreadGroup = ({
  thread,
  isLatestThread,
  companyName,
  onRegenerateAIResponse,
  isRegenerating,
  regeneratingMessageId,
  orderNumber = "",
  userEmail = "",
  sellerEmail = "",
  latestAIMessage,
  expandedThreads,
  toggleThread
}: ThreadGroupProps) => {
  const isRegeneratedThread = thread.hasMultipleVersions;
  const { getTypeIndicator, getMessageSummary } = useThreadHelpers();
  
  // Only show expanded view for the latest thread if it's not a regenerated thread
  // For regenerated threads, we always collapse them and only show latest version expanded
  if (isLatestThread && !isRegeneratedThread) {
    return (
      <LatestThreadGroup
        thread={thread}
        companyName={companyName}
        onRegenerateAIResponse={onRegenerateAIResponse}
        isRegenerating={isRegenerating}
        regeneratingMessageId={regeneratingMessageId}
        orderNumber={orderNumber}
        userEmail={userEmail}
        sellerEmail={sellerEmail}
        latestAIMessage={latestAIMessage}
        getTypeIndicator={getTypeIndicator}
      />
    );
  }
  
  // For regenerated threads, display the latest version expanded and others collapsed
  if (isLatestThread && isRegeneratedThread) {
    return (
      <RegeneratedLatestThreadGroup 
        thread={thread}
        companyName={companyName}
        onRegenerateAIResponse={onRegenerateAIResponse}
        isRegenerating={isRegenerating}
        regeneratingMessageId={regeneratingMessageId}
        orderNumber={orderNumber}
        userEmail={userEmail}
        sellerEmail={sellerEmail}
        latestAIMessage={latestAIMessage}
        getTypeIndicator={getTypeIndicator}
      />
    );
  }
  
  // For older threads, show a compact collapsible view
  const isExpanded = expandedThreads[thread.threadId];
  
  return (
    <CollapsedThreadGroup
      thread={thread}
      companyName={companyName}
      orderNumber={orderNumber}
      userEmail={userEmail}
      sellerEmail={sellerEmail}
      getTypeIndicator={getTypeIndicator}
      getMessageSummary={getMessageSummary}
      isExpanded={isExpanded}
      onToggle={() => toggleThread(thread.threadId)}
    />
  );
};

export default ThreadGroup;
